/**
 * ChatCommandProcessor
 * Handles slash command parsing and execution from the chat input.
 *
 * Extracted from Game.tsx to reduce component size.
 * Takes callback interfaces instead of direct React state setters.
 *
 * Network operations are routed through EventBus for decoupled architecture.
 *
 * Rule (plan 13 §6): every intercepted command answers in chat — a result or
 * its usage. Unknown commands fall through to the server, which answers too.
 */

import { Helix2000Character } from '../../shared/CharacterTypes';
import { gameLogger } from '../../utils/GameLogger';
import { windowBridge } from '../ui/framework/windowBridge';
import { EventBus, EVENTS } from '../../shared/EventBus';
import { canExecuteCommand } from '../../shared/constants/GMPermissions';
import { GameScene } from '../scenes/GameScene';
import { ChatHandler } from '../handlers/chat/ChatHandler';
import { helpLines } from './chatCommandHelp';
import { CLASS_NAMES, parseClassArg, parseSpeedArg, parseTeleportArgs, spawnMobCommand } from './devChatCommands';
import { livePlayerPosition } from '../systems/player/livePlayerPosition';
import { readPlayerVitals } from '../systems/player/livePlayerVitals';

export interface ChatCommandDeps {
  getPlayer: () => Helix2000Character | null;
  getGmLevel: () => number;
  getGameWorld: () => GameScene | null;
  setPlayer: (updater: (prev: Helix2000Character | null) => Helix2000Character | null) => void;
  handleDevAction: (action: string, data?: any) => void;
}

export class ChatCommandProcessor {
  private eventBus: EventBus;

  constructor(private deps: ChatCommandDeps) {
    this.eventBus = EventBus.getInstance();
  }

  /** Update dependencies (called when React state changes) */
  updateDeps(deps: Partial<ChatCommandDeps>): void {
    Object.assign(this.deps, deps);
  }

  /** Process a chat message. Returns true if handled locally (slash command). */
  process(text: string): boolean {
    if (text.startsWith('/')) {
      const parts = text.slice(1).split(' ');
      const command = parts[0];
      const args = parts.slice(1).join(' ');

      gameLogger.debug('game', `🔧 [Chat Command] /${command}`, { args: args || '' });

      const handled = this.handleCommand(command, args);
      if (handled) return true;
      // Fall through for unknown commands — send to server
    }

    // Regular chat message - emit intent event for NetworkSyncSystem to handle
    const player = this.deps.getPlayer();
    if (!player) return false;

    // Emit chat intent - NetworkSyncSystem will forward to server if connected
    this.eventBus.emit(EVENTS.PLAYER_CHAT_INTENT, { message: text });
    return true;
  }

  private handleCommand(command: string, args: string): boolean {
    const gmLevel = this.deps.getGmLevel();

    // Client-side permission filter (server also enforces)
    if (!canExecuteCommand(gmLevel, command)) {
      ChatHandler.getInstance().systemMessage(`Insufficient permissions for /${command}.`);
      return true; // Handled (blocked) — don't send to server
    }

    const { setPlayer, handleDevAction, getGameWorld, getPlayer } = this.deps;
    const player = getPlayer();
    const gameWorld = getGameWorld();

    switch (command) {
    case 'help': {
      const chat = ChatHandler.getInstance();
      chat.systemMessage('=== Available Commands ===');
      helpLines(gmLevel).forEach(line => chat.systemMessage(line));
      chat.systemMessage('=======================');
      return true;
    }
    case 'debug_state':
      handleDevAction('debug_state');
      ChatHandler.getInstance().systemMessage('[Debug] Network state written to the browser console.');
      return true;
    case 'tracker':
      windowBridge.toggle('projectileTracker');
      return true;
    case 'bank':
      windowBridge.toggle('bank');
      return true;
    case 'jobs':
      windowBridge.toggle('jobs');
      return true;
    case 'spawn':
    case 'spawnmob':
      // One spawn path (plan 13 §6): /spawn used to emit an event nothing heard.
      spawnMobCommand(args, ChatHandler.getInstance());
      return true;
    case 'spawn_test':
      handleDevAction('spawn_test', args || '0');
      ChatHandler.getInstance().systemMessage(`→ /spawn_test ${args || '0'}`);
      return true;
    case 'spawn_all_test':
      handleDevAction('spawn_all_test');
      return true;
    // These commands mutate authoritative schema state. The old client-only
    // branches changed React immediately but left the engine player and every
    // observer untouched, creating exactly the split HP/MP/nameplate state the
    // live-vitals lane is meant to prevent. Preserve one route: server command
    // -> schema patch -> React and engine projections.
    case 'heal':
    case 'damage':
    case 'mana':
    case 'xp':
    case 'gold':
    case 'level':
    case 'levelup':
    case 'kill':
      this.eventBus.emit(EVENTS.PLAYER_CHAT_INTENT, {
        message: `/${command}${args ? ` ${args}` : ''}`,
      });
      return true;
    case 'class': {
      const heroClass = parseClassArg(args);
      if (!heroClass) {
        ChatHandler.getInstance().systemMessage(`Usage: /class <${CLASS_NAMES.join('|')}>`);
        return true;
      }
      ChatHandler.getInstance().systemMessage(`🔄 Switching class to ${heroClass}...`);
      handleDevAction('set_class', heroClass);
      return true;
    }
    case 'god':
    case 'invincible':
      // /god and /invincible are SYNONYMS — the server merges both into one
      // authoritative `godMode` toggle (ChatCommandHandler). Keep all three
      // representations in lock-step so the two commands can never interfere:
      // toggle BOTH client guards together (PlayerManager god mode + the
      // isInvincible collision skip) AND forward the command so the server flips
      // its godMode. Combat is server-authoritative, so the local flags alone
      // can't stop damage — the forward is what actually makes you invincible
      // (the /god-died-in-P3 bug). Toggling both here also closes a latent gap:
      // /god used to leave isInvincible off (client collision still hit you) and
      // /invincible used to leave PlayerManager god mode off.
      handleDevAction('toggle_god_mode');
      setPlayer(prev => prev ? { ...prev, isInvincible: !prev.isInvincible } : null);
      this.eventBus.emit(EVENTS.PLAYER_CHAT_INTENT, { message: `/${command}` });
      return true;
    case 'noclip':
      handleDevAction('toggle_noclip'); // answers in chat with the new state
      return true;
    case 'speed': {
      const mult = parseSpeedArg(args);
      if (mult === null) {
        ChatHandler.getInstance().systemMessage('Usage: /speed <multiplier> — e.g. /speed 1.5 (1 = normal)');
        return true;
      }
      handleDevAction('set_speed_mult', mult); // answers in chat
      return true;
    }
    case 'killall':
    case 'clear':
      // The server's map wipe answers in chat (memory-mode servers only).
      handleDevAction('clear_mobs');
      return true;
    case 'stats':
      if (player) {
        const chat = ChatHandler.getInstance();
        const v = readPlayerVitals(player);
        chat.systemMessage(`[Stats] ${player.name} — level ${player.level} ${player.class}, HP ${v.health}/${v.maxHealth}, MP ${v.mana}/${v.maxMana}, XP ${player.xp}, gold ${player.inventory.gold}`);
        const at = livePlayerPosition(player.position);
        chat.systemMessage(`[Stats] Position (${Math.round(at.x)}, ${Math.round(at.y)}); full stats in the browser console.`);
        gameLogger.info('game', '📊 Player Stats', { name: player.name, stats: player.stats });
      } else {
        ChatHandler.getInstance().systemMessage('[Stats] No player loaded yet.');
      }
      return true;
    case 'tp': {
      // Spaces or a comma (plan 13 §6: "/tp 7700 1300" used to vanish silently).
      const point = parseTeleportArgs(args);
      if (!point) {
        ChatHandler.getInstance().systemMessage('Usage: /tp <x> <y> — e.g. /tp 7700 1300 (a comma works too)');
        return true;
      }
      ChatHandler.getInstance().systemMessage(`→ Teleporting to (${point.x}, ${point.y})`);
      handleDevAction('teleport', point);
      return true;
    }
    case 'map':
    case 'load':
      if (args) {
        ChatHandler.getInstance().systemMessage(`🗺️ Loading map: ${args}...`);
        handleDevAction('change_map', args);
      } else {
        ChatHandler.getInstance().systemMessage('Usage: /load [mapId]');
      }
      return true;
    case 'debugmap':
      const msData = gameWorld?.getMapSystem();
      if (!msData) {
        ChatHandler.getInstance().systemMessage('[Map State] ❌ MapSystem is NOT registered in the Game Kernel.');
        // Debug dump: What is registered?
        if (gameWorld) {
           handleDevAction('debug_kernel');
        }
        return true;
      }
      const currentMap = msData.getCurrentLevel();
      if (currentMap) {
        ChatHandler.getInstance().systemMessage(`[Map State] ✅ Loaded: ${currentMap.name} (${currentMap.id})`);
        ChatHandler.getInstance().systemMessage(`- Components: ${currentMap.platforms?.length || 0} Platforms, ${currentMap.entities?.length || 0} Entities, ${currentMap.spawnPoints?.length || 0} Spawns`);
        ChatHandler.getInstance().systemMessage(`- Bounds: ${currentMap.width}x${currentMap.height}px`);
      } else {
        ChatHandler.getInstance().systemMessage('[Map State] ⚠️ MapSystem is active but currentLevel is NULL. (Check MongoDB/API connection)');
      }
      return true;
    case 'kernel-systems':
      if (gameWorld) {
        handleDevAction('debug_kernel');
      } else {
        ChatHandler.getInstance().systemMessage('[Kernel] Game scene not available yet.');
      }
      return true;
    case 'maps':
      import('../services/LevelService').then(({ LevelService }) => {
        const levels = LevelService.getInstance().getAvailableMaps();
        const chat = ChatHandler.getInstance();
        chat.systemMessage(`=== Available Maps (${levels.length}) ===`);
        levels.slice(0, 15).forEach(l => chat.systemMessage(`- ${l.id}: ${l.name}${l.isBuiltIn ? ' 🏛️' : ' ☁️'}`));
        if (levels.length > 15) chat.systemMessage(`...and ${levels.length - 15} more.`);
      });
      return true;
    case 'mapinfo':
      if (args) {
        import('../services/LevelService').then(async ({ LevelService }) => {
          const chat = ChatHandler.getInstance();
          chat.systemMessage(`🔍 Fetching info for map: ${args}...`);
          const info = await LevelService.getInstance().loadLevel(args);
          if (info) {
             chat.systemMessage(`[MapInfo] Name: ${info.name}, ID: ${info.id}`);
             chat.systemMessage(`- Type: ${info.type || 'standard'}, Dimensions: ${info.width}x${info.height}`);
             chat.systemMessage(`- Platforms: ${info.platforms?.length || 0}, Entities: ${info.entities?.length || 0}`);
          } else {
             chat.systemMessage(`❌ Map '${args}' not found.`);
          }
        });
      } else {
        ChatHandler.getInstance().systemMessage('Usage: /mapinfo [mapId]');
      }
      return true;
    case 'platforms':
      if (gameWorld) {
        const ms = gameWorld.getMapSystem();
        const ps = ms?.getPlatforms() || [];
        const chat = ChatHandler.getInstance();
        chat.systemMessage(`=== Current Map Platforms (${ps.length}) ===`);
        if (ps.length > 0) {
          let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
          ps.forEach(p => {
            minX = Math.min(minX, p.x);
            maxX = Math.max(maxX, p.x + p.width);
            minY = Math.min(minY, p.y);
            maxY = Math.max(maxY, p.y);
          });
          chat.systemMessage(`- Content Bounds: X[${Math.round(minX)} to ${Math.round(maxX)}], Y[${Math.round(minY)} to ${Math.round(maxY)}]`);
          ps.slice(0, 5).forEach((p, i) => chat.systemMessage(`- [${i}] (${p.x}, ${p.y}) w:${p.width} type:${p.type}`));
        }
      } else {
        ChatHandler.getInstance().systemMessage('[Platforms] Game scene not available yet.');
      }
      return true;
    case 'reloadmap':
      if (gameWorld) {
        const ms = gameWorld.getMapSystem();
        const currentId = ms?.getCurrentLevel()?.id || gameWorld.currentLevelId;
        if (currentId) {
          ChatHandler.getInstance().systemMessage(`🔄 Reloading map: ${currentId}...`);
          handleDevAction('change_map', currentId);
        } else {
          ChatHandler.getInstance().systemMessage('❌ Could not determine current map ID to reload.');
        }
      } else {
        ChatHandler.getInstance().systemMessage('[Reload] Game scene not available yet.');
      }
      return true;
    case 'renderstate':
      // Read camera state via ICamera (CameraSystem); display size from the
      // host canvas; live FPS is in the 📊 Debug Info overlay (useFps).
      if (gameWorld) {
        const gw = gameWorld as any;
        const host = (window as any).__GAME_HOST__;
        const camMain = gw.cameras?.main;
        const camStr = camMain
          ? `(${Math.round(camMain.scrollX)}, ${Math.round(camMain.scrollY)}), Zoom: ${camMain.zoom}`
          : 'n/a (use ICamera)';
        const dispW = host?.canvas?.width ?? 'n/a';
        const dispH = host?.canvas?.height ?? 'n/a';
        ChatHandler.getInstance().systemMessage(`[Render State] Camera: ${camStr}, Display: ${dispW}x${dispH} (live FPS in 📊 Debug Info)`);
      } else {
        ChatHandler.getInstance().systemMessage('[Render State] Game Scene not available.');
      }
      return true;
    case 'renderdump':
      // Phase H10 Arc λ.9 Stage D-2: Phaser children list gone with the
      // scene-plugin tree. Substrate-agnostic equivalent (Pixi stage children)
      // would route through PixiOverlayHost.app.stage.children — deferred.
      if (gameWorld) {
        const gw = gameWorld as any;
        const children = gw.children?.list as Array<{ type?: string }> | undefined;
        if (!children) {
          ChatHandler.getInstance().systemMessage('[Render Dump] Phaser children list unavailable post-Stage-D — use Pixi stage diagnostics.');
        } else {
          let sprites = 0; let images = 0; let graphics = 0; let text = 0; let other = 0;
          for (const child of children) {
            if (child.type === 'Sprite') sprites++;
            else if (child.type === 'Image') images++;
            else if (child.type === 'Graphics') graphics++;
            else if (child.type === 'Text') text++;
            else other++;
          }
          ChatHandler.getInstance().systemMessage(
            `[Render Dump] Total Objects: ${children.length} | Sprites: ${sprites} | Images(BG): ${images} | Graphics: ${graphics} | Text: ${text} | Other: ${other}`
          );
        }
      } else {
        ChatHandler.getInstance().systemMessage('[Render Dump] Game Scene not attached.');
      }
      return true;
    case 'datastate':
      if (player) {
         const at = livePlayerPosition(player.position);
         ChatHandler.getInstance().systemMessage(`[Data State] Player DB_ID: ${player.id}, Pos: (${Math.round(at.x)}, ${Math.round(at.y)}), Level: ${player.level}`);
      } else {
         ChatHandler.getInstance().systemMessage(`[Data State] Player object is null.`);
      }
      return true;
    default:
      return false; // Unknown command, let it fall through to server
    }
  }
}
